import React, { useMemo } from 'react';

import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';

import OlcTooltip from '~/components/General/OlcTooltip';
import { ContactInfoCIcon } from '~/assets/images';

import { DASHBOARD_TOOLTIP_CLASSES } from '../../constants';
import {
  formatAverageDays,
  formatAverageValue,
  formatCurrency,
  formatNumber,
  getMailSummaryViewModel,
  getProductMixViewModel,
} from '../../utils';

const MailInsights = ({ mailData }) => {
  const summary = getMailSummaryViewModel(mailData);
  const deliverySpeed = mailData?.deliverySpeed;
  const deliverySpeedPieces = Number(deliverySpeed?.overall?.pieces) || 0;
  const productMix = useMemo(
    () => getProductMixViewModel(mailData?.productMix, summary.mailPiecesSent),
    [mailData?.productMix, summary.mailPiecesSent]
  );

  return (
    <section className="insight-grid" aria-label="Mail insights">
      <article className="dashboard-card product-mix-card">
        <h2>Product mix</h2>
        {productMix.entries.length ? (
          <div className="product-mix-content">
            <div className="product-donut" style={{ background: productMix.background }}>
              <span />
            </div>
            <div className="product-legend">
              {productMix.entries.map((item) => (
                <div key={item.name}>
                  <i style={{ background: item.color }} />
                  <span>{item.name}</span>
                  <strong>{Math.round(item.percentage)}%</strong>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="card-empty-state">
            <strong>No mail sent yet</strong>
            <span>Your product breakdown appears once you&apos;ve mailed.</span>
          </div>
        )}
      </article>

      <article className="dashboard-card delivery-speed-card">
        <h2>Delivery speed</h2>
        {deliverySpeedPieces ? (
          <div className="delivery-speed-content">
            <div className="delivery-speed-primary">
              <strong>{formatAverageValue(deliverySpeed.overall.averageDays)}</strong>
              <span>days</span>
            </div>
            <p className="delivery-speed-description">
              average to reach the mailbox
              <span aria-hidden="true">/</span>
              {Math.round(Number(deliverySpeed.overall.withinEightDaysPercent) || 0)}% within 8 days
            </p>
            <div className="delivery-speed-breakdown">
              <div>
                <span>First class</span>
                <strong>{formatAverageDays(deliverySpeed.firstClass?.averageDays)}</strong>
              </div>
              <div>
                <span>Standard class</span>
                <strong>{formatAverageDays(deliverySpeed.standardClass?.averageDays)}</strong>
              </div>
            </div>
          </div>
        ) : (
          <div className="card-empty-state">
            <strong>No delivered mail yet</strong>
            <span>Delivery timing appears after eligible pieces are marked delivered.</span>
          </div>
        )}
      </article>

      <article className="dashboard-card address-quality-card">
        <h2>Address quality</h2>
        <div className="quality-removed">
          <div className="quality-label">
            Removed before mailing
            <span className="quality-shield" aria-hidden="true">
              <GppGoodOutlinedIcon />
            </span>
            <OlcTooltip
              classes={DASHBOARD_TOOLTIP_CLASSES}
              title="Undeliverable addresses caught by address checks when you placed orders - you weren't charged to send them."
            >
              <img
                className="quality-info-icon"
                src={ContactInfoCIcon}
                alt="About removed before mailing"
              />
            </OlcTooltip>
          </div>
          <div className="quality-removed-value">
            <div>
              <strong>{formatNumber(summary.removedBeforeDelivery)}</strong>
              <span>{summary.removedBeforeDelivery === 1 ? 'address' : 'addresses'}</span>
            </div>
            <em>~{formatCurrency(summary.estimatedAddressSavings)} saved</em>
          </div>
        </div>
        <div className="quality-returned">
          <div className="quality-label">
            Returned after delivery
            <OlcTooltip
              classes={DASHBOARD_TOOLTIP_CLASSES}
              title="Returned pieces from completed orders as a percentage of all mailpieces sent."
            >
              <img
                className="quality-info-icon"
                src={ContactInfoCIcon}
                alt="About returned after delivery"
              />
            </OlcTooltip>
          </div>
          <div className="quality-value">
            <strong>{summary.returnRate.toFixed(1)}%</strong>
            <span>{formatNumber(summary.returned)} pieces</span>
            <em className={summary.returnRateLevel.toLowerCase()}>{summary.returnRateLevel}</em>
          </div>
          <div className="quality-band">
            <span className="normal" />
            <span className="fill" style={{ width: `${Math.min(summary.returnRate * 5, 100)}%` }} />
            <span
              className="marker"
              style={{ left: `${Math.min(summary.returnRate * 5, 100)}%` }}
            />
          </div>
        </div>
      </article>
    </section>
  );
};

export default MailInsights;
